import React from 'react'
import {Button} from 'react-bootstrap'
import {useTranslation} from 'react-i18next'
import {AuditPaneContext} from '../../context/AuditPane'
import {UserContext} from '../../context/User'
import GroupTabs from './GroupTabs'
import UserEditBox from './UserEditBox'

function AuditDetailTab({audit, permission}) {
  const {t} = useTranslation()

  const paneState = React.useContext(AuditPaneContext)
  const currentUser = React.useContext(UserContext)

  const getProgressBarWidth = () =>
    audit.total_items_count !== 0 && audit.total_items_count !== 'A'
      ? `${(audit.scanned_items_count * 100) / audit.total_items_count}%`
      : '0%'

  const compareFunction = (a, b) => {
    const firstVal = a.user_name.toUpperCase()
    const secondVal = b.user_name.toUpperCase()

    if (firstVal > secondVal) return 1
    if (firstVal < secondVal) return -1
    return 0
  }

  const renderAssignedUsers = () => {
    if (audit.audit_users_attributes && audit.audit_users_attributes.length > 0) {
      return audit.audit_users_attributes.sort(compareFunction).map((item, index) => (
        <span key={index} className="ap-pillbox ap-pillbox--20 ap-pillbox--white mb_5">
          {item.user_id === currentUser.details.id
            ? item.user_name + ` (${t('audit.detail-tab.labels.self')})`
            : item.user_name}
        </span>
      ))
    } else {
      return null
    }
  }

  const user_edit_permission = permission
    ? audit.audit_type_for_user === 'created' || currentUser.details.is_admin
    : false

  return (
    <div className="audit-detail-tab container">
      <div className="row">
        <div className="ap-progress">
          <div
            className="ap-progress__bar"
            role="progressbar"
            aria-valuenow={audit.scanned_items_count !== 'N' ? audit.scanned_items_count : 0}
            aria-valuemin="0"
            aria-valuemax={audit.total_items_count !== 'A' ? audit.total_items_count : 0}
            style={{width: getProgressBarWidth()}}
          >
            <span className="ap-h-sr">
              {`${getProgressBarWidth()} ${t('audit.detail-tab.progress-bar.screen-reader')}`}
            </span>
          </div>
        </div>
        <div
          className={
            'ap-progress__label' +
            `${
              audit.scanned_items_count === 0 || audit.scanned_items_count === 'N'
                ? ''
                : ' ap-progress__label--red'
            }`
          }
        >
          {audit.scanned_items_count === 'N'
            ? 'N/A'
            : `${audit.scanned_items_count} ${t('audit.detail-tab.progress-bar.of')} ${
                audit.total_items_count
              } ${t('audit.detail-tab.progress-bar.completed')}`}
        </div>
      </div>

      {!user_edit_permission && (
        <div className="row pt_12">
          <div className="audit-detail-tab__label">{t('audit.detail-tab.labels.methods')}</div>
          <div className="audit-detail-tab__label audit-detail-tab__label--value">
            {t(`audit.methods.${audit.method}`)}
          </div>
        </div>
      )}

      <div className="row pt_12">
        <div className="col-md-6 pl_0">
          <div className="audit-detail-tab__label">{t('audit.detail-tab.labels.start_date')}</div>
          <div className="audit-detail-tab__label audit-detail-tab__label--value">
            {audit.start_date}
          </div>
        </div>
        {audit.due_date && audit.due_date !== '12/31/2100' && (
          <div className="col-md-6 pl_0">
            <div className="audit-detail-tab__label">{t('audit.detail-tab.labels.due_date')}</div>
            <div className="audit-detail-tab__label audit-detail-tab__label--value">
              {audit.due_date}
            </div>
          </div>
        )}
      </div>
      <div className="row pt_12">
        <div className="audit-detail-tab__label">
          {t('audit.detail-tab.labels.scan_after_due_date')}
        </div>
        <div className="audit-detail-tab__label audit-detail-tab__label--value">
          {audit.allow_scanning_after_due_date ? 'Yes' : 'No'}
        </div>
      </div>

      {!user_edit_permission && (
        <div className="row pt_16">
          <div className="pl_0 width_100">
            <div className="ap-checkbox">
              <div className="ap-checkbox__control">
                <input
                  type="checkbox"
                  className="ap-checkbox__input_disabled"
                  checked={audit.gps_location}
                  onChange={() => {}}
                />
                <label className="ap-checkbox__label">
                  {t('audit.detail-tab.labels.record-gps')}
                </label>
              </div>
              <div className="ap-checkbox__control">
                <input
                  type="checkbox"
                  className="ap-checkbox__input_disabled"
                  checked={audit.asset_details}
                  onChange={() => {}}
                />
                <label className="ap-checkbox__label">
                  {t('audit.detail-tab.labels.show-asset')}
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row pt_10">
        <div className="audit-detail-tab__label">{t('audit.detail-tab.labels.group')}</div>
      </div>
      <div className="audit-expand__column row">
        <GroupTabs data={audit.audit_entities_attributes} />
      </div>

      {user_edit_permission && (
        <div className="mt_20">
          <UserEditBox audit={audit} />
        </div>
      )}

      {!user_edit_permission ? (
        <div className="row mt_15">
          <div className="audit-detail-tab__label">{t('audit.detail-tab.labels.assign-users')}</div>
          <div className="audit-detail-tab__scroll col-12 pl_0">{renderAssignedUsers()}</div>
        </div>
      ) : (
        <div className="row mt_15">
          <div className="audit-detail-tab__label">{t('audit.detail-tab.labels.assign-by')}</div>
          <div className="audit-detail-tab__scroll col-12 pl_0">
            <span className="ap-pillbox ap-pillbox--20 ap-pillbox--white mb_5">
              {audit.user_name}
            </span>
          </div>
        </div>
      )}

      {audit.status === 'completed' && !audit.individual_audit && (
        <>
          <div className="row mt_15">
            <div className="audit-detail-tab__label">
              {t('audit.detail-tab.labels.audit_closed_date')}
            </div>
            <div className="audit-detail-tab__label audit-detail-tab__label--value">
              {audit.closed_date ? audit.closed_date : 'N/A'}
            </div>
          </div>
          <div className="row mt_15">
            <div className="audit-detail-tab__label">
              {t('audit.detail-tab.labels.audit_closed_by')}
            </div>
            <div className="audit-detail-tab__label audit-detail-tab__label--value">
              {audit.closed_by_user ? audit.closed_by_user : 'N/A'}
            </div>
          </div>
        </>
      )}

      <div className="text_center mt_25">
        <Button onClick={paneState.openExpand} className="audit-detail-tab__button row">
          <i
            className="audit-icon--expand audit-icon--14 audit-icon--white mr_8"
            aria-hidden="true"
          ></i>
          {t('audit.detail-tab.labels.expand')}
        </Button>
      </div>
    </div>
  )
}

export default AuditDetailTab
